export interface SkinParameter {
  expectedRange: string;
  patientScore: string | number;
}

export const SEVERITY_LEVELS = {
  HIGH: 'HIGH',
  LOW: 'LOW',
} as const;

export const SKIN_PARAMETER_KEYS = {
  SEBUM_BALANCE: 'sebumBalanceParameters',
  INFLAMMATION: 'inflammationParameters',
  SENSITIVITY: 'sensitivityParameters',
  SKIN_RENEWAL: 'skinRenewalParameters',
  SKIN_RADIANCE: 'skinRadianceParameters',
  SKIN_DAMAGE: 'skinDamageParameters',
} as const;

export const HAIR_PARAMETER_KEYS = {
  HAIR_DENSITY: 'hairDensityParameters',
  HAIR_STRENGTH: 'hairStrengthParameters',
  SCALP_OIL_BALANCE: 'scalpOilBalanceParameters',
  HAIR_SMOOTHNESS: 'hairSmoothnessParameters',
  PREMATURE_GREYING: 'prematureGreyingParameters',
  GREYING: 'greyingParameters',
} as const;

export type SeverityLevel = (typeof SEVERITY_LEVELS)[keyof typeof SEVERITY_LEVELS];
export type SkinParameterKey = (typeof SKIN_PARAMETER_KEYS)[keyof typeof SKIN_PARAMETER_KEYS];
export type HairParameterKey = (typeof HAIR_PARAMETER_KEYS)[keyof typeof HAIR_PARAMETER_KEYS];
export type ReportParameterKey = SkinParameterKey | HairParameterKey;
export type SkinParametersInfo = Record<SkinParameterKey, SkinParameter>;
export type HairParametersInfo = Record<HairParameterKey, SkinParameter>;

export interface ParameterViewModel {
  key: string;
  label?: string;
  scorePercentage: number;
  patientScore: string;
  targetRange: string;
  severity: SeverityLevel | null;
  isInRange: boolean;
}

export interface AiParameterInput {
  name?: string;
  valuePercent?: number;
  targetRange?: string;
  notScored?: boolean;
}

const SKIN_PARAMETER_KEYS_ORDER: SkinParameterKey[] = [
  SKIN_PARAMETER_KEYS.SEBUM_BALANCE,
  SKIN_PARAMETER_KEYS.INFLAMMATION,
  SKIN_PARAMETER_KEYS.SENSITIVITY,
  SKIN_PARAMETER_KEYS.SKIN_RENEWAL,
  SKIN_PARAMETER_KEYS.SKIN_RADIANCE,
  SKIN_PARAMETER_KEYS.SKIN_DAMAGE,
];

const HAIR_PARAMETER_KEYS_ORDER: HairParameterKey[] = [
  HAIR_PARAMETER_KEYS.HAIR_DENSITY,
  HAIR_PARAMETER_KEYS.HAIR_STRENGTH,
  HAIR_PARAMETER_KEYS.SCALP_OIL_BALANCE,
  HAIR_PARAMETER_KEYS.HAIR_SMOOTHNESS,
  HAIR_PARAMETER_KEYS.PREMATURE_GREYING,
  HAIR_PARAMETER_KEYS.GREYING,
];

export function buildUserSkinParameters(skinParameters: SkinParametersInfo | null | undefined): ParameterViewModel[] {
  if (!skinParameters) return [];

  return SKIN_PARAMETER_KEYS_ORDER
    .filter((key): boolean => hasValidParameterData(skinParameters[key]))
    .map((key): ParameterViewModel | null => createParameterViewModel(key, skinParameters[key]))
    .filter((item): item is ParameterViewModel => item !== null);
}

export function buildUserHairParameters(hairParameters: HairParametersInfo | null | undefined): ParameterViewModel[] {
  if (!hairParameters) return [];

  return HAIR_PARAMETER_KEYS_ORDER
    .filter((key): boolean => hasValidParameterData(hairParameters[key]))
    .map((key): ParameterViewModel | null => createParameterViewModel(key, hairParameters[key]))
    .filter((item): item is ParameterViewModel => item !== null);
}

export function buildAiParameters(parameters: AiParameterInput[] | null | undefined): ParameterViewModel[] {
  return (parameters ?? [])
    .map((parameter): ParameterViewModel | null => createAiParameterViewModel(parameter))
    .filter((item): item is ParameterViewModel => item !== null);
}

function createAiParameterViewModel(parameter: AiParameterInput): ParameterViewModel | null {
  if (parameter.notScored || !parameter.name) return null;

  const parsedRange = parseExpectedRange((parameter.targetRange ?? '').replaceAll('%', ''));
  const value = Number(parameter.valuePercent);
  if (!parsedRange || Number.isNaN(value)) return null;

  const [minExpectedScore, maxExpectedScore] = parsedRange;
  // Range test uses the same rounded value the badge displays, so the badge
  // can never show an in-range number in the out-of-range color.
  const displayedScore = Math.round(value);
  const isInRange = displayedScore >= minExpectedScore && displayedScore <= maxExpectedScore;

  return {
    key: parameter.name,
    label: parameter.name,
    scorePercentage: Math.max(0, Math.min(100, displayedScore)),
    patientScore: `${displayedScore}%`,
    targetRange: `${minExpectedScore}-${maxExpectedScore}%`,
    severity: getSeverity(isInRange, displayedScore, maxExpectedScore),
    isInRange,
  };
}

function hasValidParameterData(parameterInfo: SkinParameter | undefined): boolean {
  if (!parameterInfo) return false;
  return parameterInfo.expectedRange !== '' && parameterInfo.patientScore !== '' && parameterInfo.patientScore !== null;
}

function createParameterViewModel(parameterKey: ReportParameterKey, parameterInfo: SkinParameter): ParameterViewModel | null {
  const parsedRange = parseExpectedRange(parameterInfo.expectedRange);
  const patientScore = Number(parameterInfo.patientScore);

  if (!parsedRange || Number.isNaN(patientScore)) return null;

  const [minExpectedScore, maxExpectedScore] = parsedRange;
  const scorePercentage = toPercentage(patientScore);
  const isInRange = patientScore >= minExpectedScore && patientScore <= maxExpectedScore;
  const severity = getSeverity(isInRange, patientScore, maxExpectedScore);

  return {
    key: parameterKey,
    scorePercentage,
    patientScore: `${Math.round(scorePercentage)}%`,
    targetRange: `${toPercentage(minExpectedScore)}-${toPercentage(maxExpectedScore)}%`,
    severity,
    isInRange,
  };
}

function getSeverity(isInRange: boolean, patientScore: number, maxExpectedScore: number): SeverityLevel | null {
  if (isInRange) return null;
  return patientScore > maxExpectedScore ? SEVERITY_LEVELS.HIGH : SEVERITY_LEVELS.LOW;
}

function parseExpectedRange(expectedRange: string): [number, number] | null {
  if (!expectedRange) return null;

  const rangeValues = expectedRange.split('-').map((value): number => Number(value.trim()));
  if (rangeValues.length !== 2 || rangeValues.some(Number.isNaN)) return null;

  const [left, right] = rangeValues;
  return left <= right ? [left, right] : [right, left];
}

function toPercentage(score: number): number {
  return Math.max(0, Math.min(100, Math.round(score * 10)));
}
